/**
 * useAdminAuth hook - auth state for admin dashboard with role checking.
 */

import { useCallback, useEffect, useState } from 'react'
import type { AuthUser, UserRole } from '@provendoro/shared/types'
import type { LoginInput } from '@provendoro/shared/validation'
import * as authClient from './auth-client.js'

export interface UseAdminAuthReturn {
  user: AuthUser | null
  loading: boolean
  error: string | null
  login: (input: LoginInput) => Promise<boolean>
  logout: () => Promise<void>
  clearError: () => void
  hasRole: (roles: UserRole[]) => boolean
}

export function useAdminAuth(): UseAdminAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    authClient.getSessionUser().then((result) => {
      if (cancelled) return
      if (result.data?.user) {
        // Only set user if they have an admin role
        if (result.data.user.role) {
          setUser(result.data.user)
        }
      }
      setLoading(false)
    }).catch(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  const login = useCallback(async (input: LoginInput): Promise<boolean> => {
    setError(null)
    setLoading(true)
    try {
      const result = await authClient.signIn(input)
      if (result.error) {
        setError(result.error.message)
        setLoading(false)
        return false
      }
      if (result.data?.user) {
        if (!result.data.user.role) {
          setError('This account does not have admin access')
          await authClient.signOut()
          setLoading(false)
          return false
        }
        setUser(result.data.user)
      }
      setLoading(false)
      return true
    } catch {
      setError('Network error. Please try again.')
      setLoading(false)
      return false
    }
  }, [])

  const logout = useCallback(async () => {
    await authClient.signOut()
    setUser(null)
  }, [])

  const clearError = useCallback(() => setError(null), [])

  const hasRole = useCallback(
    (roles: UserRole[]): boolean => {
      if (!user?.role) return false
      return roles.includes(user.role)
    },
    [user],
  )

  return { user, loading, error, login, logout, clearError, hasRole }
}
